import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ExternalLink, Link2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_DRIVE_LINKS, getDriveLinkLabel, isGoogleDriveUrl } from '../../lib/taskTypes';

/**
 * Read-only list of Drive links. Links always open in a new browser tab.
 */
export const DriveLinksList = ({ links = [], onRemove, disabled = false, compact = false }) => {
  if (links.length === 0) return null;
  return (
    <ul className={compact ? 'flex flex-wrap gap-1.5' : 'space-y-1.5'} data-testid="drive-links-list">
      {links.map((link) => (
        <li
          key={link.url}
          className={compact
            ? 'inline-flex'
            : 'flex items-center justify-between gap-2 border border-border px-3 py-2'}
        >
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center gap-1.5 min-w-0 text-primary hover:underline ${compact ? 'text-xs' : 'text-sm'}`}
            title={link.url}
          >
            <Link2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{getDriveLinkLabel(link)}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
          </a>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => onRemove(link.url)}
              disabled={disabled}
              aria-label={`Remove ${getDriveLinkLabel(link)}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * Controlled editor for a task's Google Drive links ({ url, label }[]).
 */
export const DriveLinksEditor = ({ value = [], onChange, disabled = false, showLabel = true }) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');

  const addLink = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!isGoogleDriveUrl(trimmed)) {
      toast.error('Enter an https Google Drive link (drive.google.com or docs.google.com)');
      return;
    }
    if (value.some(link => link.url === trimmed)) {
      toast.error('This link is already added');
      return;
    }
    if (value.length >= MAX_DRIVE_LINKS) {
      toast.error(`You can add up to ${MAX_DRIVE_LINKS} Drive links`);
      return;
    }
    onChange([...value, { url: trimmed, label: label.trim() }]);
    setUrl('');
    setLabel('');
  };

  const handleKeyDown = (e) => {
    // Enter inside the task form must add the link, not submit the form.
    if (e.key === 'Enter') {
      e.preventDefault();
      addLink();
    }
  };

  return (
    <div className="space-y-2" data-testid="drive-links-editor">
      {showLabel && <Label>GOOGLE DRIVE LINKS</Label>}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://drive.google.com/..."
          disabled={disabled}
          className="sm:flex-[2]"
          data-testid="drive-link-url-input"
        />
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Label (optional)"
          maxLength={200}
          disabled={disabled}
          className="sm:flex-1"
          data-testid="drive-link-label-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={addLink}
          disabled={disabled || !url.trim()}
          data-testid="drive-link-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          ADD
        </Button>
      </div>
      <DriveLinksList
        links={value}
        onRemove={(removeUrl) => onChange(value.filter(link => link.url !== removeUrl))}
        disabled={disabled}
      />
    </div>
  );
};
